import {useState, useEffect, useRef, useLayoutEffect, useCallback} from "react";

/**
 * Priority Plus: calcula dinámicamente cuántos items del menú caben en el ancho
 * disponible del contenedor y devuelve el resto para el menú hamburguesa.
 *
 * La medición de anchos se hace sobre un contenedor off-screen (todos los items
 * visibles) para que el ancho real de cada item no dependa de lo que se esté
 * mostrando en ese momento en la barra visible.
 */
const useOverflowMenu = (items = [], options = {}) => {
    const {
        safetyMargin = 12,
        gap = 0,
        debounceMs = 120,
        hamburgerFallbackWidth = 44,
        maxRetries = 30
    } = options;

    const containerRef = useRef(null);
    const hamburgerRef = useRef(null);
    const itemMeasureRefs = useRef([]);
    const itemsRef = useRef(items);
    const rafRef = useRef(null);
    const retriesRef = useRef(0);

    const [visibleCount, setVisibleCount] = useState(items.length);
    const [isMeasured, setIsMeasured] = useState(false);

    itemsRef.current = items;

    // Firma estable: evita recalcular cuando el array cambia de referencia pero no de contenido
    const itemsKey = items.map(item => item.to || item.text).join("|");

    const calculate = useCallback(() => {
        const currentItems = itemsRef.current;
        const container = containerRef.current;

        if (!container || currentItems.length === 0) {
            setVisibleCount(currentItems.length);
            setIsMeasured(true);
            return;
        }

        const itemWidths = itemMeasureRefs.current
            .slice(0, currentItems.length)
            .map(el => (el ? el.offsetWidth : 0));

        const hasAllWidths = itemWidths.length === currentItems.length
            && itemWidths.every(width => width > 0);

        if (!hasAllWidths) {
            // Los refs aún no están listos: reintentamos en el siguiente frame
            retriesRef.current += 1;
            if (retriesRef.current <= maxRetries) {
                rafRef.current = window.requestAnimationFrame(calculate);
            } else {
                // Fallback: mostramos todos los items para no dejar el menú vacío
                setVisibleCount(currentItems.length);
                setIsMeasured(true);
            }
            return;
        }

        retriesRef.current = 0;

        const totalItemsWidth = itemWidths.reduce((acc, width, index) => {
            return acc + width + (index > 0 ? gap : 0);
        }, 0);

        const containerWidth = container.clientWidth;

        // Todos los items caben: no hace falta hamburguesa
        if (totalItemsWidth <= containerWidth - safetyMargin) {
            setVisibleCount(currentItems.length);
            setIsMeasured(true);
            return;
        }

        // No caben todos: reservamos el ancho del botón hamburguesa
        const hamburgerEl = hamburgerRef.current;
        const hamburgerWidth = hamburgerEl && hamburgerEl.offsetWidth > 0
            ? hamburgerEl.offsetWidth
            : hamburgerFallbackWidth;

        const availableWidth = containerWidth - hamburgerWidth - safetyMargin;

        let usedWidth = 0;
        let count = 0;
        for (let index = 0; index < itemWidths.length; index++) {
            const width = itemWidths[index] + (index > 0 ? gap : 0);
            if (usedWidth + width > availableWidth) {
                break;
            }
            usedWidth += width;
            count++;
        }

        setVisibleCount(count);
        setIsMeasured(true);
    }, [gap, safetyMargin, hamburgerFallbackWidth, maxRetries]);

    // Cálculo inicial y cuando cambian los items o las opciones
    useLayoutEffect(() => {
        retriesRef.current = 0;
        calculate();
    }, [calculate, itemsKey]);

    // Recalculado al cambiar el ancho disponible
    useEffect(() => {
        let timeoutId = null;

        const onResize = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(() => {
                retriesRef.current = 0;
                calculate();
            }, debounceMs);
        };

        window.addEventListener("resize", onResize);

        let resizeObserver = null;
        if (typeof ResizeObserver !== "undefined" && containerRef.current) {
            resizeObserver = new ResizeObserver(onResize);
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener("resize", onResize);
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [calculate, debounceMs]);

    // Limpieza
    useEffect(() => {
        return () => {
            if (rafRef.current) {
                window.cancelAnimationFrame(rafRef.current);
            }
        };
    }, []);

    const setMeasureRef = useCallback((index, el) => {
        itemMeasureRefs.current[index] = el;
    }, []);

    return {
        containerRef,
        hamburgerRef,
        setMeasureRef,
        visibleItems: items.slice(0, visibleCount),
        overflowItems: items.slice(visibleCount),
        hasOverflow: visibleCount < items.length,
        visibleCount,
        isMeasured
    };
};

export default useOverflowMenu;
